import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import { userAPI, adminAPI } from '../../services/api';
import UserModal from '../../components/Modals/UserModal';
import Modal from '../../components/Modals/Modal';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll({ role: 'client' });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users list');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordClick = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handleSavePassword = async () => {
    if (newPassword.trim().length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    try {
      setIsResettingPassword(true);
      await adminAPI.changeUserPassword(userToResetPassword.id, newPassword);
      alert(`Password changed successfully for ${userToResetPassword.name}!`);
      setIsPasswordModalOpen(false);
      setUserToResetPassword(null);
    } catch (err) {
      console.error(err);
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const toggleStatus = async (user) => {
    try {
      const newStatus = user.status === 'blocked' ? 'active' : 'blocked';
      await userAPI.update(user.id, { status: newStatus });
      fetchUsers();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert('Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <AdminDashboard title="User Management">
      <div className="p-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <div className="relative w-full md:w-96">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">search</span>
            <input
              type="text"
              placeholder="Search users..."
              className="w-full pl-12 pr-4 py-3 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-medium placeholder-[var(--text-secondary)]/50"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button 
              onClick={fetchUsers}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--surface-white)] border border-[var(--border-light)] rounded-2xl text-sm font-bold text-[var(--text-secondary)] hover:bg-[var(--off-white)] transition-all shadow-sm"
            >
              <span className="material-symbols-outlined text-lg">refresh</span>
              Refresh
            </button>
            <button 
              onClick={() => { setSelectedUser(null); setIsModalOpen(true); }}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-2xl text-sm font-black hover:bg-blue-700 transition-all shadow-md shadow-blue-500/20"
            >
              <span className="material-symbols-outlined text-lg">person_add</span>
              Add User
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[var(--text-secondary)] font-bold">Fetching user data...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-3xl p-8 text-center text-red-600 dark:text-red-400">
            <span className="material-symbols-outlined text-4xl mb-2">error</span>
            <p className="font-bold">{error}</p>
            <button onClick={fetchUsers} className="mt-4 underline font-bold">Try again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[var(--border-light)]">
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">User</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">City</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Status</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Joined</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-light)]/30">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="group hover:bg-[var(--off-white)]/50 transition-colors">
                      <td className="py-5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center overflow-hidden border-2 border-[var(--surface-white)] shadow-sm font-black text-blue-400">
                            {user.avatarUrl ? (
                              <img src={`${process.env.REACT_APP_API_URL}${user.avatarUrl}`} alt="" className="w-full h-full object-cover" />
                            ) : (
                              user.name?.charAt(0).toUpperCase() || 'U'
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-[var(--text-primary)]">{user.name}</div>
                            <div className="text-xs text-[var(--text-secondary)] font-medium">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-4 text-sm font-medium text-[var(--text-secondary)]">
                        {user.city || '-'}
                      </td>
                      <td className="py-5 px-4 text-sm">
                        <span className={`px-3 py-1 rounded-full font-bold text-[10px] uppercase tracking-wider ${
                          user.status === 'blocked' 
                            ? 'bg-red-500/10 text-red-600 dark:text-red-400' 
                            : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                        }`}>
                          {user.status === 'blocked' ? 'Blocked' : (user.isWaitlisted ? 'Waitlisted' : 'Active')}
                        </span>
                      </td>
                      <td className="py-5 px-4 text-sm font-bold text-[var(--text-secondary)]/70">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="py-5 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => toggleStatus(user)}
                            className={`p-2 rounded-xl transition-all ${
                              user.status === 'blocked'
                                ? 'text-green-500 hover:bg-green-500/10'
                                : 'text-red-400 hover:bg-red-500/10'
                            }`}
                            title={user.status === 'blocked' ? 'Unblock user' : 'Block user'}
                          >
                            <span className="material-symbols-outlined">
                              {user.status === 'blocked' ? 'person_check' : 'person_off'}
                            </span>
                          </button>
                          <button 
                            onClick={() => handleResetPasswordClick(user)}
                            className="p-2 text-[var(--text-secondary)]/50 hover:text-yellow-500 hover:bg-yellow-500/10 rounded-xl transition-all"
                            title="Change password"
                          >
                            <span className="material-symbols-outlined">key</span>
                          </button>
                          <button 
                            onClick={() => { setSelectedUser(user); setIsModalOpen(true); }}
                            className="p-2 text-[var(--text-secondary)]/50 hover:text-blue-500 hover:bg-blue-500/10 rounded-xl transition-all"
                            title="Edit user"
                          >
                            <span className="material-symbols-outlined">edit</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-20 text-center text-[var(--text-secondary)]/50 font-medium italic">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <UserModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          user={selectedUser} 
          onSave={fetchUsers} 
          defaultRole="client" 
        />

        <Modal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} title="Change Password" maxWidth="max-w-md">
          <div className="space-y-6">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              Set a new password for <span className="font-bold text-[var(--text-primary)]">{userToResetPassword?.name}</span> ({userToResetPassword?.email}). The password must be at least 6 characters.
            </p>

            {passwordError && (
              <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl text-orange-500 text-xs font-bold flex items-start gap-2">
                <span className="material-symbols-outlined text-base shrink-0">warning</span>
                <span className="leading-normal">{passwordError}</span>
              </div>
            )}

            {/* Honeypot to prevent browser autofill on search inputs */}
            <input type="text" name="username" style={{ display: 'none' }} autoComplete="username" />
            <input type="password" name="password" style={{ display: 'none' }} autoComplete="new-password" />

            <div>
              <label className="block text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-2">
                New Password
              </label>
              <input 
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordError('');
                }}
                placeholder="••••••••"
                disabled={isResettingPassword}
                className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-tangerine/30 focus:border-tangerine transition-all"
              />
            </div>

            <div className="flex gap-3 pt-2 border-t border-[var(--border-light)]/40">
              <button
                onClick={() => setIsPasswordModalOpen(false)}
                disabled={isResettingPassword}
                className="flex-1 py-2.5 text-sm font-bold text-[var(--text-secondary)] bg-[var(--surface-white)] border border-[var(--border-light)] rounded-xl hover:bg-[var(--off-white)] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePassword}
                disabled={isResettingPassword}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-tangerine hover:bg-[#d97700] rounded-xl shadow shadow-tangerine/20 hover:-translate-y-0.5 disabled:transform-none disabled:opacity-75 transition-all flex items-center justify-center gap-2"
              >
                {isResettingPassword && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {isResettingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminDashboard>
  );
};

export default AdminUsers;
